import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { CalendarDays, Phone, ArrowRight, CheckCircle2, Clock } from "lucide-react";
import { SITE, CONTACT_TOPICS } from "../data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-sm text-slate-800 outline-none transition-colors placeholder:text-slate-400 focus:border-gold focus:ring-2 focus:ring-gold/30";

const BookingSection = () => {
  const [data, setData] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [form, setForm] = useState({ name: "", phone: "", email: "", sex: "", ageRange: "", topic: "", note: "", privacy: false });
  const [status, setStatus] = useState("idle");
  const [booked, setBooked] = useState(null);

  const load = () => {
    setLoadError(false);
    axios
      .get(`${API}/appointments/availability`)
      .then((r) => setData(r.data))
      .catch(() => setLoadError(true));
  };

  useEffect(() => {
    load();
  }, []);

  const selectedDay = data?.days.find((d) => d.date === selectedDate);
  const set = (key) => (e) =>
    setForm((f) => ({ ...f, [key]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setStatus("loading");
    try {
      const r = await axios.post(`${API}/appointments`, {
        date: selectedDate,
        time: selectedTime,
        name: form.name,
        phone: form.phone,
        email: form.email,
        sex: form.sex,
        age_range: form.ageRange,
        topic: form.topic,
        note: form.note || null,
        privacy: form.privacy,
      });
      setBooked(r.data);
      setStatus("success");
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: "booking_appointment" });
      if (typeof window.gtag === "function") {
        window.gtag("event", "conversion", { send_to: "AW-18465206934/ILmMCMmSi4AdEJbI8uRE" });
      }
    } catch (err) {
      setStatus("idle");
      const detail = err?.response?.data?.detail;
      toast.error(
        typeof detail === "string"
          ? detail
          : "Invio non riuscito. Riprova o contatta lo studio telefonicamente."
      );
      if (err?.response?.status === 409) {
        setSelectedTime(null);
        load();
      }
    }
  };

  const dayBtnCls = (d) =>
    `rounded-xl border px-3 py-3 text-left transition-all duration-200 ${
      d.full
        ? "cursor-not-allowed border-slate-200 bg-slate-50 text-slate-400"
        : selectedDate === d.date
        ? "border-navy bg-navy text-white shadow-md"
        : "border-slate-300 bg-white text-navy hover:border-gold hover:shadow-sm"
    }`;

  const slotBtnCls = (s) =>
    `flex items-center justify-center gap-2 rounded-xl border px-3 py-3 text-sm font-semibold transition-all duration-200 ${
      !s.available
        ? "cursor-not-allowed border-slate-200 bg-slate-50 text-slate-400 line-through"
        : selectedTime === s.time
        ? "border-gold bg-gold text-navy shadow-md"
        : "border-slate-300 bg-white text-navy hover:border-gold"
    }`;

  return (
    <section id="prenota" data-testid="booking-section" className="bg-navy py-16 sm:py-24 text-white hero-lines">
      <div className="container-site grid items-start gap-10 lg:grid-cols-2">
        <div>
          <p className="kicker">Appuntamenti</p>
          <h2 className="mt-3 font-serif text-3xl sm:text-4xl font-semibold">Prenota un appuntamento</h2>
          <p className="mt-4 max-w-lg text-base leading-relaxed text-slate-300">
            Scegli autonomamente un giorno e un orario disponibili per richiedere un
            appuntamento con l'Avv. Manfredi Bruno.
          </p>
          <p className="mt-3 max-w-lg text-sm leading-relaxed text-slate-400">
            L'agenda dello studio riserva ai nuovi clienti finestre dedicate il{" "}
            <strong className="text-gold-light">lunedì</strong> e il{" "}
            <strong className="text-gold-light">mercoledì</strong>: verifica le disponibilità
            dei prossimi 10 giorni e invia la tua richiesta.
          </p>
          <div className="mt-7 flex flex-wrap gap-3">
            <a href={SITE.phoneHref} data-testid="booking-call-button" className="btn-gold">
              <Phone size={16} aria-hidden="true" /> Chiama lo studio
            </a>
            <Link to="/contatti" data-testid="booking-callback-link" className="btn-outline-light">
              Richiedi di essere ricontattato <ArrowRight size={16} aria-hidden="true" />
            </Link>
          </div>
        </div>

        <div data-testid="booking-widget" className="rounded-2xl bg-white p-5 text-navy shadow-2xl sm:p-7">
          {status === "success" && booked ? (
            <div data-testid="booking-success" className="flex flex-col items-center py-8 text-center">
              <CheckCircle2 size={48} className="text-gold-dark" aria-hidden="true" />
              <p className="mt-4 font-serif text-2xl font-semibold text-navy">
                Richiesta di appuntamento inviata
              </p>
              <p className="mt-3 max-w-md text-sm leading-relaxed text-slate-600">
                La tua richiesta di appuntamento è stata ricevuta. Sarai ricontattato dalla
                segreteria per confermare l'appuntamento e ricevere tutte le informazioni
                necessarie.
              </p>
              <p className="mt-5 rounded-xl bg-gold-soft px-5 py-3 text-sm font-semibold text-navy">
                {booked.label} · ore {booked.time}
              </p>
            </div>
          ) : !data && !loadError ? (
            <div className="flex min-h-[280px] items-center justify-center text-sm text-slate-500" data-testid="booking-loading">
              Caricamento delle disponibilità...
            </div>
          ) : loadError ? (
            <div className="flex min-h-[280px] flex-col items-center justify-center text-center" data-testid="booking-load-error">
              <p className="text-sm text-slate-600">
                Non è stato possibile caricare le disponibilità.
              </p>
              <button type="button" onClick={load} data-testid="booking-retry-button" className="btn-outline-navy mt-4">
                Riprova
              </button>
            </div>
          ) : (
            <div>
              <div className="flex items-center gap-3">
                <CalendarDays size={22} className="text-gold-dark" aria-hidden="true" />
                <p className="font-serif text-xl font-semibold">Disponibilità dei prossimi 10 giorni</p>
              </div>

              {data.days.length === 0 || data.days.every((d) => d.full) ? (
                <p className="mt-5 text-sm leading-relaxed text-slate-600" data-testid="booking-no-availability">
                  Al momento non ci sono orari disponibili nei prossimi giorni. Puoi contattare
                  lo studio al {SITE.phone} o lasciare i tuoi dati nella pagina Contatti.
                </p>
              ) : (
                <>
                  <p className="mt-6 text-xs font-bold uppercase tracking-[0.15em] text-gold-dark">
                    1. Scegli il giorno
                  </p>
                  <div className="mt-3 grid grid-cols-2 gap-2.5">
                    {data.days.map((d) => (
                      <button
                        key={d.date}
                        type="button"
                        data-testid={`booking-day-${d.date}`}
                        disabled={d.full}
                        onClick={() => {
                          setSelectedDate(d.date);
                          setSelectedTime(null);
                        }}
                        className={dayBtnCls(d)}
                      >
                        <span className="block text-sm font-semibold">{d.label}</span>
                        <span className="mt-0.5 block text-xs opacity-80">
                          {d.full
                            ? "Completo"
                            : `${d.slots.filter((s) => s.available).length} orari disponibili`}
                        </span>
                      </button>
                    ))}
                  </div>

                  {selectedDay && (
                    <>
                      <p className="mt-6 text-xs font-bold uppercase tracking-[0.15em] text-gold-dark">
                        2. Scegli l'orario
                      </p>
                      <div className="mt-3 grid grid-cols-3 gap-2.5">
                        {selectedDay.slots.map((s) => (
                          <button
                            key={s.time}
                            type="button"
                            data-testid={`booking-slot-${s.time.replace(":", "")}`}
                            disabled={!s.available}
                            onClick={() => setSelectedTime(s.time)}
                            className={slotBtnCls(s)}
                          >
                            <Clock size={14} aria-hidden="true" />
                            {s.available ? s.time : "Prenotato"}
                          </button>
                        ))}
                      </div>
                    </>
                  )}

                  {selectedDay && selectedTime && (
                    <form onSubmit={submit} data-testid="booking-form" className="mt-6 space-y-4">
                      <p className="text-xs font-bold uppercase tracking-[0.15em] text-gold-dark">
                        3. I tuoi dati
                      </p>
                      <div className="flex items-center justify-between rounded-xl bg-gold-soft px-4 py-2.5 text-sm">
                        <span className="font-semibold text-navy">
                          {selectedDay.label} · ore {selectedTime}
                        </span>
                        <button
                          type="button"
                          data-testid="booking-change-selection"
                          onClick={() => {
                            setSelectedDate(null);
                            setSelectedTime(null);
                          }}
                          className="text-xs font-semibold text-gold-dark underline underline-offset-2"
                        >
                          Modifica
                        </button>
                      </div>
                      <input
                        type="text"
                        required
                        minLength={2}
                        maxLength={100}
                        value={form.name}
                        onChange={set("name")}
                        className={inputCls}
                        placeholder="Nome e cognome *"
                        aria-label="Nome e cognome"
                        data-testid="booking-name-input"
                        autoComplete="name"
                      />
                      <input
                        type="tel"
                        required
                        minLength={5}
                        maxLength={30}
                        value={form.phone}
                        onChange={set("phone")}
                        className={inputCls}
                        placeholder="Numero di telefono *"
                        aria-label="Numero di telefono"
                        data-testid="booking-phone-input"
                        autoComplete="tel"
                      />
                      <input
                        type="email"
                        required
                        value={form.email}
                        onChange={set("email")}
                        className={inputCls}
                        placeholder="Indirizzo email *"
                        aria-label="Indirizzo email"
                        data-testid="booking-email-input"
                        autoComplete="email"
                      />
                      <div className="grid grid-cols-2 gap-3">
                        <select
                          required
                          value={form.sex}
                          onChange={set("sex")}
                          className={inputCls}
                          aria-label="Sesso"
                          data-testid="booking-sex-select"
                        >
                          <option value="" disabled>Sesso *</option>
                          <option value="Uomo">Uomo</option>
                          <option value="Donna">Donna</option>
                          <option value="Altro">Altro</option>
                        </select>
                        <select
                          required
                          value={form.ageRange}
                          onChange={set("ageRange")}
                          className={inputCls}
                          aria-label="Fascia di età"
                          data-testid="booking-age-select"
                        >
                          <option value="" disabled>Fascia di età *</option>
                          <option value="18-30">18-30</option>
                          <option value="31-50">31-50</option>
                          <option value="51+">51+</option>
                        </select>
                      </div>
                      <select
                        required
                        value={form.topic}
                        onChange={set("topic")}
                        className={inputCls}
                        aria-label="Argomento"
                        data-testid="booking-topic-select"
                      >
                        <option value="" disabled>Argomento *</option>
                        {CONTACT_TOPICS.map((t) => (
                          <option key={t} value={t}>{t}</option>
                        ))}
                      </select>
                      <textarea
                        rows={3}
                        maxLength={1000}
                        value={form.note}
                        onChange={set("note")}
                        className={inputCls}
                        placeholder="Breve descrizione del motivo della richiesta (facoltativa)"
                        aria-label="Motivo della richiesta"
                        data-testid="booking-note-textarea"
                      />
                      <label className="flex cursor-pointer items-start gap-3 text-xs leading-relaxed text-slate-600">
                        <input
                          type="checkbox"
                          required
                          checked={form.privacy}
                          onChange={set("privacy")}
                          className="mt-0.5 h-4 w-4 shrink-0 accent-gold"
                          data-testid="booking-privacy-checkbox"
                        />
                        <span>
                          Ho letto la{" "}
                          <Link to="/privacy-policy" data-testid="booking-privacy-link" className="font-semibold text-gold-dark underline underline-offset-2">
                            Privacy Policy
                          </Link>{" "}
                          e acconsento al trattamento dei miei dati per la gestione della richiesta di appuntamento. *
                        </span>
                      </label>
                      <button
                        type="submit"
                        disabled={status === "loading"}
                        data-testid="booking-submit-button"
                        className="btn-gold w-full disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        {status === "loading" ? "Invio in corso..." : "Conferma la richiesta di appuntamento"}
                      </button>
                      <p className="text-xs leading-relaxed text-slate-500">
                        L'appuntamento sarà confermato dalla segreteria dello studio.
                      </p>
                    </form>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default BookingSection;
